import { useCallback, useEffect, useRef, useState } from "react";
import { forceSignOut } from "../services/utils/auth-interceptor";

/** Time of inactivity before showing the warning modal (ms) */
export const IDLE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

/** Seconds the modal counts down before auto-logout */
export const COUNTDOWN_DURATION_S = 60;

/** Minimum interval between mousemove resets (ms) */
const MOUSEMOVE_THROTTLE_MS = 30_000;

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
	"mousedown",
	"keydown",
	"scroll",
	"touchstart",
	"mousemove",
];

export function useIdleTimer() {
	const [showWarning, setShowWarning] = useState(false);
	const [secondsRemaining, setSecondsRemaining] = useState(COUNTDOWN_DURATION_S);

	const idleTimerRef = useRef<ReturnType<typeof setTimeout>>();
	const countdownRef = useRef<ReturnType<typeof setInterval>>();
	const lastMouseMoveRef = useRef(0);
	const showWarningRef = useRef(false);

	const clearTimers = useCallback(() => {
		if (idleTimerRef.current) {
			clearTimeout(idleTimerRef.current);
			idleTimerRef.current = undefined;
		}
		if (countdownRef.current) {
			clearInterval(countdownRef.current);
			countdownRef.current = undefined;
		}
	}, []);

	const startIdleTimer = useCallback(() => {
		if (idleTimerRef.current) {
			clearTimeout(idleTimerRef.current);
		}
		idleTimerRef.current = setTimeout(() => {
			showWarningRef.current = true;
			setShowWarning(true);
			setSecondsRemaining(COUNTDOWN_DURATION_S);

			countdownRef.current = setInterval(() => {
				setSecondsRemaining((prev) => {
					if (prev <= 1) {
						clearTimers();
						forceSignOut();
						return 0;
					}
					return prev - 1;
				});
			}, 1000);
		}, IDLE_TIMEOUT_MS);
	}, [clearTimers]);

	const continueSession = useCallback(() => {
		clearTimers();
		showWarningRef.current = false;
		setShowWarning(false);
		setSecondsRemaining(COUNTDOWN_DURATION_S);
		startIdleTimer();
	}, [clearTimers, startIdleTimer]);

	useEffect(() => {
		const handleActivity = (event: Event) => {
			// Don't reset if the warning modal is showing
			if (showWarningRef.current) return;

			if (event.type === "mousemove") {
				const now = Date.now();
				if (now - lastMouseMoveRef.current < MOUSEMOVE_THROTTLE_MS) return;
				lastMouseMoveRef.current = now;
			}

			startIdleTimer();
		};

		ACTIVITY_EVENTS.forEach((event) => {
			window.addEventListener(event, handleActivity, { passive: true });
		});

		startIdleTimer();

		return () => {
			ACTIVITY_EVENTS.forEach((event) => {
				window.removeEventListener(event, handleActivity);
			});
			clearTimers();
		};
	}, [startIdleTimer, clearTimers]);

	return { showWarning, secondsRemaining, continueSession };
}
